import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RTDatepicker from 'react-tailwindcss-datepicker';
import moment from 'moment';
import type { Moment } from 'moment';

interface DatepickerProps {
    value?: Moment;
    defaultValue: Moment;
    onChange: (value: Moment) => void;
    direction?: any;
    from?: Moment;
    inputClassName?: string;
    unclearable?: boolean;
    /** When false, calendar opens on pointer interaction only (not Tab/focus). Default true. */
    openOnFocus?: boolean;
};

const POPOVER_SELECTOR = 'div.absolute.z-10';

const hideCalendarPopover = (root: HTMLElement) => {
    const popover = root.querySelector<HTMLElement>(POPOVER_SELECTOR);
    if (!popover || !popover.classList.contains('block'))
        return;

    popover.classList.remove('block', 'translate-y-0', 'opacity-1');
    popover.classList.add('translate-y-4', 'opacity-0', 'hidden');
};

const supportsPopoverApi = () =>
    typeof HTMLElement !== 'undefined'
    && typeof HTMLElement.prototype.showPopover === 'function';

const isCalendarOpen = (popover: HTMLElement) =>
    popover.classList.contains('block') && !popover.classList.contains('hidden');

const clearPopoverLayout = (popover: HTMLElement) => {
    [
        'position', 'inset', 'left', 'top', 'right', 'margin',
        'border', 'padding', 'background', 'overflow', 'z-index',
    ].forEach((prop) => popover.style.removeProperty(prop));
};

const positionPortaledPopover = (root: HTMLElement, popover: HTMLElement) => {
    const anchor = root.getBoundingClientRect();
    const width = popover.offsetWidth || 320;
    const height = popover.offsetHeight || 320;
    const gap = 4;
    let left = anchor.left;
    let top = anchor.bottom + gap;

    if (left + width > window.innerWidth - 8)
        left = Math.max(8, anchor.right - width);
    if (left < 8)
        left = 8;

    if (top + height > window.innerHeight - 8)
        top = Math.max(8, anchor.top - height - gap);

    popover.style.setProperty('position', 'fixed');
    popover.style.setProperty('inset', 'unset');
    popover.style.setProperty('left', `${left}px`);
    popover.style.setProperty('top', `${top}px`);
    popover.style.setProperty('right', 'auto');
    popover.style.setProperty('margin', '0');
    popover.style.setProperty('border', 'none');
    popover.style.setProperty('padding', '0');
    popover.style.setProperty('background', 'transparent');
    popover.style.setProperty('overflow', 'visible');
    popover.style.setProperty('z-index', '10000');
};

const Datepicker = (props: DatepickerProps) => {
    const { t, i18n } = useTranslation();

    const {
        defaultValue, onChange, direction, from, inputClassName, unclearable,
        openOnFocus = true
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const openAllowedRef = useRef(false);

    const [value, setValue] = useState(defaultValue ? {
        startDate: (defaultValue || moment()).toDate(),
        endDate: (defaultValue || moment()).toDate(),
    } : null);

    const handleValueChange = (newValue: any) => {
        setValue(newValue);
        onChange(moment(newValue.startDate));
    }

    useEffect(() => {
        if (props.value) {
            setValue({
                startDate: props.value.toDate(),
                endDate: props.value.toDate(),
            });
            return;
        }

        // Clear local state when the controlled value becomes empty (e.g. navigate
        // from a card with a deadline to one without) — otherwise the previous
        // date stays visible until a full remount/refresh.
        setValue(null);
    }, [props.value]);

    useEffect(() => {
        if (openOnFocus)
            return;

        const root = containerRef.current;
        if (!root)
            return;

        const allowOpen = () => {
            openAllowedRef.current = true;
        };

        const onFocusIn = () => {
            if (openAllowedRef.current) {
                openAllowedRef.current = false;
                return;
            }
            // Library opens on focus; close again when focus came from keyboard.
            queueMicrotask(() => hideCalendarPopover(root));
        };

        root.addEventListener('pointerdown', allowOpen);
        root.addEventListener('focusin', onFocusIn);

        return () => {
            root.removeEventListener('pointerdown', allowOpen);
            root.removeEventListener('focusin', onFocusIn);
        };
    }, [openOnFocus]);

    // Escape overflow:hidden ancestors (change modal tab panels) via the Popover API
    // top layer — same idea as MultiSelect's Headless UI Portal.
    useEffect(() => {
        const root = containerRef.current;
        if (!root || !supportsPopoverApi())
            return;

        const findPopover = () => root.querySelector<HTMLElement>(POPOVER_SELECTOR);

        const sync = () => {
            const popover = findPopover();
            if (!popover)
                return;

            if (!popover.hasAttribute('popover'))
                popover.setAttribute('popover', 'manual');

            const open = isCalendarOpen(popover);
            const popoverOpen = popover.matches(':popover-open');

            if (open) {
                if (!popoverOpen) {
                    try {
                        popover.showPopover();
                    } catch {
                        // Element may already be transitioning; ignore.
                    }
                }
                positionPortaledPopover(root, popover);
                return;
            }

            if (popoverOpen) {
                try {
                    popover.hidePopover();
                } catch {
                    // ignore
                }
            }
            clearPopoverLayout(popover);
        };

        const observer = new MutationObserver(sync);
        observer.observe(root, { attributes: true, subtree: true, attributeFilter: ['class'] });
        window.addEventListener('resize', sync);
        window.addEventListener('scroll', sync, true);
        sync();

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', sync);
            window.removeEventListener('scroll', sync, true);
            const popover = findPopover();
            if (!popover)
                return;
            if (popover.matches(':popover-open')) {
                try {
                    popover.hidePopover();
                } catch {
                    // ignore
                }
            }
            clearPopoverLayout(popover);
            popover.removeAttribute('popover');
        };
    }, []);

    const popoverDirection = direction === 'up' || direction === 'down'
        ? direction
        : undefined;
    // Library only understands up/down; "left" means align the calendar to the
    // input's right edge so it grows into available space (fallback without Popover API).
    const alignEnd = direction === 'left';

    return (
        <div ref={containerRef}>
            <RTDatepicker
                i18n={i18n.language}
                startWeekOn="mon"
                asSingle={true}
                useRange={false}
                startFrom={from?.toDate()}
                popoverDirection={popoverDirection}
                value={value}
                onChange={handleValueChange}
                containerClassName={`relative ${unclearable ? 'unclearable' : ''}${alignEnd ? ' [&>div.absolute.z-10]:right-0' : ''}`}
                inputClassName={`relative transition-all duration-300 py-2.5 pl-4 w-40 max-w-full tracking-wide rounded font-bold text-sm placeholder-gray-400 focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-blue-500/20 ${inputClassName}`}
                displayFormat={t('common.input_date_format') || ''}
            />
        </div>
    );
}

export default Datepicker;
